import {
  useContext,
  useRef,
  useState,
  useEffect
} from 'react';
import { Transition } from 'react-transition-group';

import MenuItemText from './MenuItemText.jsx';
import SideNavMenu from './SideNavMenu.jsx';
import { SideNavContext } from './SideNavContext.jsx';
import { deepCurrent, isDeepActive } from './SideNav.helpers.js';
import LdsIcon from '../LdsIcon/index.jsx';

export default function SubMenu({
  menuItem,
  menuDepth,
  parentId,
  routerLink,
}) {
  const [isShowingSubMenu, setIsShowingSubMenu] = useState(false);
  const [menuHeight, setMenuHeight] = useState(null);

  const {
    setActive,
    setScrollContainerOverflow,
    isMobile,
    showMobileMenu,
  } = useContext(SideNavContext);

  const menuRef = useRef(null);

  const checkIsShowing = (item) => {
    return menuItem.active
      || (('subMenu' in menuItem) && isDeepActive(item.subMenu))
      || menuItem.current
      || (('link' in menuItem) && ('subMenu' in menuItem) && deepCurrent(item.subMenu));
  };

  useEffect(() => {
    // Calculate the max height of the menu before we close it so we can show a transition
    if (menuRef.current && !menuHeight) {
      setMenuHeight(menuRef.current.scrollHeight);
    }
  }, [menuRef]);

  useEffect(() => {
    // Only close the menu after we calculate the max height initially
    if (menuHeight) {
      const isShowing = checkIsShowing(menuItem);
      setIsShowingSubMenu(isShowing);
    }
  }, [menuItem, menuHeight]);

  const commonStyles = {
    overflow: 'hidden',
    transition: 'all 300ms ease-in-out',
  };

  /*
    Due to the deeply nested nature of the side nav calculating an exact max height for each sub menu can be difficult.
    In order to animate the expand / collapse we use a best guess max height - and then remove it so no content gets cut off.
    We have to add it back and then set it to 0 when collapsing.
  */
  const transitionStyles = {
    entering: { maxHeight: menuHeight },
    entered: { maxHeight: 'none' },
    exiting: { maxHeight: menuHeight },
    exited: { maxHeight: '0px' },
  };

  const handleOnEnter = () => {
    /* When the sub menu is opened in mobile we need to set the overflow of .lds-side-nav-scroll-container
      to hidden so that the sub menu is full screen.
    */
    if (isMobile && menuDepth === 1) setScrollContainerOverflow('hidden');
  };

  const handleOnLeave = () => {
    if (isMobile) setScrollContainerOverflow('scroll');
  };

  useEffect(() => {
    if (isMobile && showMobileMenu && isShowingSubMenu && menuDepth === 1) {
      handleOnEnter();
    }
  }, [showMobileMenu, isMobile, isShowingSubMenu, menuDepth]);

  if (!menuItem.subMenu) return '';

  return (
    <Transition
      nodeRef={menuRef}
      in={isShowingSubMenu}
      timeout={300}
      onEnter={handleOnEnter}
      onExited={handleOnLeave}
    >
      { state => (
        <div
            ref={menuRef}
            id={`sub-menu-${menuItem.id}`}
            className="lds-side-nav-sub-menu-container"
            style={{
              ...commonStyles,
              ...transitionStyles[state],
              visibility: isShowingSubMenu ? 'visible' : 'hidden',
            }}
            aria-hidden={isShowingSubMenu ? 'false' : 'true'}
          >
            {
              menuDepth === 1
              && <button
                className="lds-button-clear-style lds-side-nav-sub-menu-back"
                onClick={() => setActive(menuItem.id)}
              >
                <LdsIcon
                  v-if="$rtl"
                  className="back-button-icon rtl-icon"
                  name="CaretRight"
                  decorative
                  inline
                />
                <LdsIcon
                  className="back-button-icon ltr-icon"
                  name="CaretLeft"
                  decorative
                  inline
                />
                <MenuItemText menuItem={menuItem} />
              </button>
            }
            <SideNavMenu
              menu={menuItem.subMenu}
              menuDepth={menuDepth + 1}
              parentId={parentId}
              routerLink={routerLink}
            />
        </div>
      )}
    </Transition>
  );
}
